import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
Button,
Chip,
Stack,
CircularProgress,
Alert
} from "@mui/material";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import InsightsIcon from "@mui/icons-material/Insights";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { formatAdminDateTime } from "../utils/adminDateTime";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import {
  adminPageSx,
  contentCardSx,
  metricCardSx,
  metricGridSx,
  textWrapSx
} from "../utils/adminResponsive";

const PLATFORM_ORDER = ["facebook", "instagram"];

const platformTitles = {
  facebook: "Facebook",
  instagram: "Instagram"
};

const statusColors = {
  success: "#2e7d32",
  error: "#d32f2f",
  unsupported: "#6d4c41",
  not_configured: "#ed6c02"
};

function formatNumber(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return Number(value).toLocaleString();
}

function buildMetricRows(snapshot) {
  return [
    { label: "Followers", value: snapshot?.followers_count },
    { label: "Following", value: snapshot?.following_count },
    { label: "Likes", value: snapshot?.likes_count },
    { label: "Comments", value: snapshot?.comments_count },
    { label: "Subscribers", value: snapshot?.subscribers_count },
    { label: "Media", value: snapshot?.media_count },
    { label: "Videos", value: snapshot?.video_count },
    { label: "Views", value: snapshot?.views_count }
  ].filter((item) => item.value !== null && item.value !== undefined);
}

export default function SocialMetrics() {
  const [latest, setLatest] = useState({});
  const [history, setHistory] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityCounts, setActivityCounts] = useState({
    comments: 0,
    follows: 0,
    likes: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const loadMetrics = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await adminFetch("/api/admin/social-metrics", {
        method: "GET"
      });

      if (data?.status) {
        setLatest(data.data?.latest_by_platform || {});
        setHistory(data.data?.recent_history || []);
        setRecentActivity(data.data?.recent_activity || []);
        setActivityCounts(data.data?.activity_counts || {
          comments: 0,
          follows: 0,
          likes: 0,
          total: 0
        });
      } else {
        setError(data?.message || "Failed to load social metrics");
      }
    } catch (loadError) {
      console.log(loadError);
      setError("Failed to load social metrics");
    }

    setLoading(false);
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setError("");

    try {
      const data = await adminFetch("/api/admin/social-metrics/sync", {
        method: "POST"
      });

      if (!data?.status) {
        setError(data?.message || "Failed to sync social metrics");
        setSyncing(false);
        return;
      }

      await loadMetrics();
    } catch (syncError) {
      console.log(syncError);
      setError("Failed to sync social metrics");
    }

    setSyncing(false);
  };

  const historyColumns = [
    {
      key: "platform",
      label: "Platform",
      render: (item) => platformTitles[item.platform] || item.platform
    },
    {
      key: "account",
      label: "Account",
      render: (item) => item.account_name || item.account_identifier || "-"
    },
    {
      key: "status",
      label: "Status",
      render: (item) => (
        <Chip
          label={(item.status || "unknown").replaceAll("_", " ")}
          sx={{
            bgcolor: statusColors[item.status] || "#455a64",
            color: "#fff",
            textTransform: "capitalize"
          }}
        />
      )
    },
    {
      key: "status_message",
      label: "Message",
      render: (item) => item.status_message || "-"
    },
    {
      key: "synced_at",
      label: "Synced At",
      render: (item) => formatAdminDateTime(item.synced_at, { fallback: "-" })
    }
  ];

  const activityColumns = [
    {
      key: "platform",
      label: "Platform",
      render: (item) => platformTitles[item.platform] || item.platform
    },
    {
      key: "activity_type",
      label: "Type",
      render: (item) => String(item.activity_type || "-").replaceAll("_", " ")
    },
    {
      key: "account_identifier",
      label: "Account",
      render: (item) => item.account_identifier || "-"
    },
    {
      key: "actor_name",
      label: "Actor",
      render: (item) => item.actor_name || "-"
    },
    {
      key: "detail",
      label: "Details",
      render: (item) => `${item.reference_title ? `${item.reference_title} - ` : ""}${item.detail || "-"}`
    },
    {
      key: "activity_time",
      label: "Activity Time",
      render: (item) => formatAdminDateTime(item.activity_time, { fallback: "-" })
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent }}>
            Social Metrics
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)", mt: 0.5, ...textWrapSx }}>
            Tracks public profile performance snapshots from configured platform APIs.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={18} sx={{ color: "#003300" }} /> : <AutorenewIcon />}
          onClick={handleSync}
          disabled={syncing}
          sx={{
            backgroundColor: Colors.accent,
            color: "#003300",
            fontWeight: 700,
            width: { xs: "100%", sm: "auto" }
          }}
        >
          {syncing ? "Syncing..." : "Sync Now"}
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ ...metricGridSx, mb: 3 }}>
          <Card sx={metricCardSx}>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Total Activity Records
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {formatNumber(activityCounts.total)}
            </Typography>
          </Card>

          <Card sx={metricCardSx}>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Comments Stored
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {formatNumber(activityCounts.comments)}
            </Typography>
          </Card>

          <Card sx={metricCardSx}>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Follow Changes
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {formatNumber(activityCounts.follows)}
            </Typography>
          </Card>

          <Card sx={metricCardSx}>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Like Changes
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {formatNumber(activityCounts.likes)}
            </Typography>
          </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 2, mb: 3, width: "100%", maxWidth: "100%" }}>
        {PLATFORM_ORDER.map((platform) => {
          const snapshot = latest[platform];
          const metricRows = buildMetricRows(snapshot);
          const status = snapshot?.status || "not_configured";

          return (
              <Card key={platform} sx={{ ...contentCardSx, p: 3, minHeight: 260 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <InsightsIcon sx={{ color: Colors.accent }} />
                    <Typography variant="h6" fontWeight={700}>
                      {platformTitles[platform]}
                    </Typography>
                  </Stack>

                  <Chip
                    label={status.replaceAll("_", " ")}
                    sx={{
                      bgcolor: statusColors[status] || "#455a64",
                      color: "#fff",
                      textTransform: "capitalize"
                    }}
                  />
                </Stack>

                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  {snapshot?.account_name || snapshot?.account_identifier || "Not configured"}
                </Typography>

                {metricRows.length ? (
                  <Stack spacing={0.5}>
                    {metricRows.map((item) => (
                      <Stack key={`${platform}-${item.label}`} direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {item.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {formatNumber(item.value)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No performance metrics available yet for this platform.
                  </Typography>
                )}

                <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
                  {snapshot?.status_message || "Awaiting first sync."}
                </Typography>

                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 2 }}>
                  Last sync: {formatAdminDateTime(snapshot?.synced_at, { fallback: "-" })}
                </Typography>
              </Card>
          );
        })}
      </Box>

      <Card sx={{ p: 2, mb: 3, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Recent Sync History
        </Typography>

        <Box sx={{ position: "relative", minHeight: 320 }}>
          <ResponsiveDataTable
            columns={historyColumns}
            rows={history}
            loading={loading}
            minWidth={780}
            emptyMinHeight={180}
            emptyMessage="No social metric snapshots yet"
            mobileTitle={(item) => platformTitles[item.platform] || item.platform}
          />

          <TableLoadingOverlay loading={loading} />
        </Box>
      </Card>

      <Card sx={{ p: 2, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Recent Activity
        </Typography>

        <Box sx={{ position: "relative", minHeight: 320 }}>
          <ResponsiveDataTable
            columns={activityColumns}
            rows={recentActivity}
            loading={loading}
            minWidth={860}
            emptyMinHeight={180}
            emptyMessage="No social activity records yet"
            mobileTitle={(item) => platformTitles[item.platform] || item.platform}
          />

          <TableLoadingOverlay loading={loading} />
        </Box>
      </Card>
    </Box>
  );
}
